/**
 * @file The one Select that picks where a source column lands, shared by
 * Mapping Review's column rows (`MappingReviewColumns`), S3's order-form
 * column mapping (`SourceCellColumns`) and S3's Parts mapping of a flat
 * pricebook header (`PricebookColumnMapping`, target `parts`). Options come from
 * `mappingReviewRankedOptions`: the Typesafe winner and up to
 * `MAPPING_ALTERNATIVES_TOP_K` runners-up, each with its confidence, then
 * every other field. With `allowClear`, clearing keeps the column out; it is
 * off by default, so only a control where clearing means "Don't import"
 * offers it (a mapped column's row, S3's column), never a picker for a new
 * mapping. Choosing the current
 * target again fires `onConfirm`, because keeping the winner as-is is how a
 * reviewer confirms it.
 */
import { CSSProperties, FC, ReactNode } from 'react';
import { Select } from 'antd';
import { mappingReviewRankedOptions } from './mappingReviewRankedOptions';
import type { RankedOptionEntry, RankedScore } from './mappingReviewRankedOptions';

const UNIVERSE_LABEL = {
  orderForm: 'All order form fields',
  parts: 'All Parts fields',
  fields: 'All fields',
} as const;

const MappingSelect: FC<{
  /** What the column maps onto; names the tier of fields the model did not rank. */
  target: keyof typeof UNIVERSE_LABEL;
  /** The current target field id; undefined when the column is not mapped. */
  value: string | undefined;
  /** Every target the model scored for this column: the winner and its alternatives. */
  scored: readonly RankedScore<string>[];
  /** Every field the column may land on; `disabled` ones show but cannot be picked. */
  fields: readonly RankedOptionEntry<string>[];
  /** A new target, or undefined when cleared. */
  onChange: (value: string | undefined) => void;
  /** The current target was chosen again. */
  onConfirm?: () => void;
  ariaLabel: string;
  /** Offer a clear control, which unmaps the column; only where that means "Don't import". */
  allowClear?: boolean;
  disabled?: boolean;
  placeholder?: string;
  suffix?: ReactNode;
  style?: CSSProperties;
}> = ({ target, value, scored, fields, onChange, onConfirm, ariaLabel, allowClear = false, disabled, placeholder, suffix, style }) => (
  <Select
    size="small"
    showSearch={{ optionFilterProp: 'label' }}
    allowClear={allowClear}
    disabled={disabled}
    placeholder={placeholder}
    aria-label={ariaLabel}
    value={value}
    suffix={suffix}
    style={{ width: '100%', ...style }}
    options={mappingReviewRankedOptions({
      scored: [...scored],
      universe: [...fields],
      universeLabel: UNIVERSE_LABEL[target],
    })}
    onSelect={(selected: string | undefined) => {
      if (selected === value) onConfirm?.();
    }}
    onChange={(next: string | undefined) => onChange(next)}
  />
);

export default MappingSelect;
